import { ThemeProvider } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToolLoader } from 'shared/components';
import defaultTheme from 'shared/defaultTheme';
import { INIT } from 'shared/wizard/actions';
import ToolContainer from 'shared/wizard/ToolContainer';
import { getSettings } from './helpers';
import { lazyWithRetry, analytics, useTrackPageview } from "shared/utility";
import { Routes, Route, useNavigate } from 'react-router-dom';

const applicantFormPromise = import(
  /* webpackChunkName: "applicant-form" */
  'shared/forms/ApplicantForm'
);
const expensesFormPromise = import(
  /* webpackChunkName: "expenses-form" */
  'shared/forms/ExpensesForm'
);
const leadInformationPromise = import(
  /* webpackChunkName: "lead-information-form" */
  'shared/forms/LeadInformationForm'
);
const partnerFormPromise = import(
  /* webpackChunkName: "partner-form" */
  'shared/forms/PartnerForm'
);
const currentMortgageFormPromise = import(
  /* webpackChunkName: "current-mortgage-form" */
  'shared/forms/CurrentMortgageForm'
);
const outputFinalPromise = import(
  /* webpackChunkName: "maxmortgage-output-final" */
  './components/OutputFinal'
);
const wizardFinalPromise = import(
  /* webpackChunkName: "maxmortgage-wizard-final" */
  './components/WizardFinal'
);
const energyLabelPromise = import(
  /* webpackChunkName: "energy-label-form" */
  'shared/forms/EnergyLabelForm'
);

const ApplicantForm = lazyWithRetry(() => applicantFormPromise);
const ExpensesForm = lazyWithRetry(() => expensesFormPromise);
const EnergyLabelForm = lazyWithRetry(() => energyLabelPromise);
const LeadInformationForm = lazyWithRetry(() => leadInformationPromise);
const PartnerForm = lazyWithRetry(() => partnerFormPromise);
const CurrentMortgageForm = lazyWithRetry(() => currentMortgageFormPromise);
const OutputFinal = lazyWithRetry(() => outputFinalPromise);
const WizardFinal = lazyWithRetry(() => wizardFinalPromise);

const App = () => {
  const { app, framed, settings = null, pages } = useSelector(state => state);
  const [theme, setTheme] = useState(defaultTheme);
  const base = `${app}${framed ? '/framed' : ''}`;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    navigate(`/${app}${framed ? '/framed' : ''}/${pages[0] + location.search}`, {
      state: { page: pages[0] },
      replace: true,
    });
  }, [framed]);

  useTrackPageview();

  useEffect(() => {
    getSettings(({ data }) => {
      dispatch({ type: INIT, payload: { ...data } });
      setTheme(theme => ({ ...theme, ...data.settings }));

      const { uaCode } = data.settings;
      analytics.init('G-WLHSYTMNNN', uaCode);
    });
  }, []);

  const outputRouteSwitch = (
    <Routes>
      <Route path={`/${base}/final`} element={<OutputFinal />} />
    </Routes>
  );
  const inputRouteSwitch = (
    <Routes>
      <Route path={`/${base}/partner`} element={<PartnerForm />} />
      {settings?.askCurrentMortgage && (
        <Route path={`/${base}/current-mortgage`} element={<CurrentMortgageForm />} />
      )}
      <Route path={`/${base}/applicant-information`} element={<LeadInformationForm />} />
      <Route path={`/${base}/expenses`} element={<ExpensesForm />} />
      <Route path={`/${base}/energy-label`} element={<EnergyLabelForm />} />
      <Route path={`/${base}/final`} element={<WizardFinal />} />
      <Route path={`/${base}/landing`} element={<ApplicantForm />} />
    </Routes>
  );

  return !settings ? (
    <ToolLoader show />
  ) : (
    <ThemeProvider theme={theme}>
      <ToolContainer Input={inputRouteSwitch} Output={outputRouteSwitch} />
    </ThemeProvider>
  );
};

export default App;
